import PageMeta from '@/components/PageMeta';
import SectionHeader from '@/components/SectionHeader';
import PhoneMockup from '@/components/PhoneMockup';
import AppPreviewGallery from '@/components/AppPreviewGallery';

const STEPS = [
  { n: '01', t: 'Prepare', b: 'Choose a supported movement, review the recording instructions, and position the phone so the relevant parts of the body are visible.' },
  { n: '02', t: 'Capture', b: 'Record or upload the movement using the workflow available in the release version.' },
  { n: '03', t: 'Analyse', b: 'The system processes visible movement patterns and the available video evidence.' },
  { n: '04', t: 'Review', b: 'You receive structured observations and supported feedback presented in plain language.' },
  { n: '05', t: 'Decide', b: 'The feedback is supportive information. Seek qualified professional advice when pain, symptoms, movement suitability or individual circumstances are uncertain.' },
];

const LIMITS = [
  'The supported movement list',
  'Camera position and angle',
  'Framing',
  'Lighting',
  'Clothing',
  'Occlusion of body parts',
  'Video quality',
  'Movement speed',
  'Body visibility',
  'Individual movement variation',
  'Final release configuration',
];

export default function HowItWorks() {
  return (
    <div>
      <PageMeta
        title="How it works"
        description="Movement feedback from the camera already in your pocket. A simple five-step flow, with clear limitations."
      />
      <section className="vl-container py-16 lg:py-24">
        <p className="mono-label text-vl-violet">How it works</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink max-w-3xl">
          Movement feedback from the camera already in your pocket.
        </h1>
        <p className="mt-6 text-lg text-vl-ink/70 max-w-2xl leading-relaxed">
          A simple flow that respects how people actually move at home, in clinics, in gyms, and in community spaces.
        </p>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-4">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="rounded-3xl bg-vl-surface border hairline p-7 lg:p-8 flex gap-6 hover:shadow-md transition-shadow"
              >
                <span className="font-mono text-sm text-vl-violet pt-1 shrink-0">{s.n}</span>
                <div>
                  <h3 className="font-serif-display text-2xl text-vl-ink leading-tight">{s.t}</h3>
                  <p className="text-sm text-vl-ink/70 mt-3 leading-relaxed">{s.b}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-5 sticky top-24">
            <PhoneMockup variant="bare" />
          </div>
        </div>
      </section>

      <section className="vl-container py-24 lg:py-32">
        <SectionHeader
          kicker="Inside the app"
          title="A look at the prototype interface."
          lead="These prototype screens are illustrative. The release version may look and behave differently, and on-screen figures or labels are unverified interface content."
        />
        <div className="mt-14">
          <AppPreviewGallery />
        </div>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-24 lg:py-28">
          <SectionHeader
            kicker="Important to know"
            title="What can affect the result."
            lead="Valanti Labs has been researched around the reality that human movement and everyday video are imperfect. The feedback you receive depends on several factors."
          />
          <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {LIMITS.map((l) => (
              <li
                key={l}
                className="rounded-2xl border hairline bg-vl-surface px-5 py-4 text-sm text-vl-ink/80 flex items-start gap-3"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-vl-violet mt-2" />
                {l}
              </li>
            ))}
          </ul>

          <div className="mt-14 rounded-3xl bg-vl-surface border hairline p-8 max-w-3xl">
            <p className="mono-label text-vl-violet mb-3">Important note</p>
            <p className="text-vl-ink/80 leading-relaxed">
              Valanti Labs is not a substitute for a doctor, physiotherapist, or other qualified professional. Please
              continue to follow professional guidance and stop if you experience concerning symptoms.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
